// Validation Types

/**
 * Structured Zod validation issue returned by the API.
 * The frontend maps these to i18n keys instead of receiving English strings.
 */
export type ZodValidationIssue =
  | { code: 'too_small'; minimum: number; type: 'string' | 'number' | 'array'; inclusive: boolean }
  | { code: 'too_big'; maximum: number; type: 'string' | 'number' | 'array'; inclusive: boolean }
  | { code: 'invalid_string'; validation: 'email' | 'url' | 'uuid' | 'datetime' | 'regex' | string }
  | { code: 'invalid_type'; expected: string; received: string }
  | { code: 'invalid_enum_value'; options: string[]; received: string }
  | { code: 'custom'; message?: string }
  | { code: 'unrecognized_keys' | 'invalid_union' | 'invalid_arguments' | 'invalid_return_type' | 'invalid_date' | 'not_multiple_of' | 'not_finite' };

/** Map of field path → structured validation issue returned by API validation errors. */
export type ApiValidationDetails = Record<string, ZodValidationIssue>;

// User Types
export interface User {
  id: string;
  email: string;
  name: string;
  role?: 'user' | 'expert' | 'admin';
  language: 'en' | 'pt-BR' | 'es';
  theme: 'matrix' | 'golden-bull' | 'cyber-purple' | 'ice';
  timezone: string;
  subscription?: {
    plan: SubscriptionPlan;
    status: 'active' | 'canceled' | 'past_due' | 'trialing';
    provider?: 'stripe' | 'mercadopago' | null;
    currentPeriodEnd?: Date;
    currentPeriodStart?: Date;
    cancelAtPeriodEnd?: boolean;
  };
  syncApiKeyCreatedAt?: Date | null;
  syncApiKeyHint?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserPreferences {
  language: 'en' | 'pt-BR' | 'es';
  theme: 'matrix' | 'golden-bull' | 'cyber-purple' | 'ice';
  timezone: string;
  defaultTimeframe: Timeframe;
  defaultAssetType: 'forex' | 'crypto' | 'indices';
}

// Subscription Types
export type SubscriptionPlan = 'free' | 'starter' | 'pro' | 'unlimited';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing';
export type PaymentProvider = 'stripe' | 'mercadopago';

export interface Subscription {
  id: string;
  userId: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  provider: PaymentProvider | null;
  externalId: string | null;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Market Data Types
export type Timeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' | '1w';
export type AssetType = 'forex' | 'crypto' | 'indices';
export type MarketProvider = 'deriv' | 'binance' | 'twelvedata' | 'oanda';

export interface Candle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface TradingSymbol {
  id: string;
  name: string;
  displayName: string;
  type: AssetType;
  provider: MarketProvider;
  pricePrecision: number;
  minQuantity?: number;
  active: boolean;
}

export interface MarketTick {
  symbol: string;
  price: number;
  timestamp: number;
  bid?: number;
  ask?: number;
  /**
   * The provider that published this tick. Consumers (e.g. the chart) MUST
   * compare this against the source of the candle data they're rendering
   * before applying the tick — mixing tick prices from one provider into
   * candles from another corrupts open/close and miscolors the bar.
   *
   * Optional for back-compat with consumers that ignore the field; new
   * publishers always set it.
   */
  source?: MarketProvider;
}

// AI Model Types
export type AIModelProvider = 'gemini' | 'openrouter';
export type AIModel =
  | 'gemini-2.5-flash'
  | 'gemini-2.5-pro'
  | 'openai/gpt-4o'
  | 'openai/gpt-4o-mini'
  | 'anthropic/claude-sonnet-4'
  | 'deepseek/deepseek-chat'
  | 'google/gemini-2.5-flash';

// Analysis Types
export type AnalysisDirection = 'LONG' | 'SHORT';
export type AnalysisMethodology = 'smc' | 'ict' | 'price-action' | 'fibonacci';
export type MarketTrend = 'bullish' | 'bearish' | 'ranging';
export type MarketPhase = 'accumulation' | 'markup' | 'distribution' | 'markdown';
export type ZoneStatus = 'fresh' | 'tested' | 'mitigated';

export interface AnalysisZone {
  type: 'bullish' | 'bearish';
  top: number;
  bottom: number;
  strength: number;
  status: ZoneStatus;
  time: number; // Unix timestamp of the candle where this zone was formed
  timeEnd?: number; // Optional end time for zones that span multiple candles
}

export interface FairValueGap {
  type: 'bullish' | 'bearish';
  top: number;
  bottom: number;
  filledPercent: number;
  time: number; // Unix timestamp of the candle where this FVG was created
}

export interface LiquidityPool {
  type: 'bsl' | 'ssl';
  price: number;
  strength: number;
  time: number; // Unix timestamp of the candle where this liquidity was identified
}

export interface ChartMarker {
  type: 'zone' | 'line' | 'label' | 'arrow';
  label: string;
  color: 'profit' | 'loss' | 'neutral' | 'accent';
  coordinates: {
    price: number;
    priceEnd?: number;
    time: number;
    timeEnd?: number;
  };
}

export interface AnalysisSetup {
  valid: boolean;
  direction: AnalysisDirection | null;
  confidence: number;
  entry: {
    price: number;
    type: 'limit' | 'market' | 'stop';
    reasoning: string;
  } | null;
  stopLoss: {
    price: number;
    reasoning: string;
  } | null;
  targets: {
    t1: { price: number; reasoning: string };
    t2: { price: number; reasoning: string };
    t3: { price: number; reasoning: string };
  } | null;
  riskReward: number | null;
  riskPercent: number | null;
  invalidation: string | null;
}

// ---------------------------------------------------------------------------
// Methodology-Specific Chart Overlays (PR #406 follow-up)
//
// Each trading methodology emits its own visual primitives (Fibonacci levels,
// ICT killzones, pin-bar markers, etc). The legacy `zones`/`structure` shape
// was SMC-only — non-SMC results were squashed into `zones.orderBlocks` which
// caused the chart to render every methodology identically.
//
// `MethodologyOverlay` is a discriminated union with one variant per
// methodology. It is OPTIONAL: when absent, the frontend falls back to the
// legacy SMC-shaped `zones`/`structure` payload (unchanged for back-compat).
// ---------------------------------------------------------------------------

export interface FibonacciLevel {
  ratio: number; // 0.382, 0.5, 0.618, 0.786 …
  price: number;
}

export interface FibonacciAnchor {
  startTime: number;
  startPrice: number;
  endTime: number;
  endPrice: number;
  /** Direction of the impulse leg this anchor was drawn on. */
  direction: 'bullish' | 'bearish';
}

export interface FibonacciOverlay {
  kind: 'fibonacci';
  anchor?: FibonacciAnchor | null;
  levels?: FibonacciLevel[];
  extensions?: FibonacciLevel[];
  /** 0.618-0.786 high-probability reaction band. */
  goldenZone?: { lowPrice: number; highPrice: number } | null;
}

export interface ICTKillzone {
  /** Canonical name, used for label + color. */
  name: 'london' | 'new-york-am' | 'london-close' | 'new-york-pm' | 'asia';
  /** Unix timestamps marking the killzone window on the chart. */
  startTime: number;
  endTime: number;
}

export interface ICTPO3Phase {
  phase: 'accumulation' | 'manipulation' | 'distribution';
  startTime: number;
  endTime: number;
  /** Optional price band so the phase can be shaded. */
  topPrice?: number;
  bottomPrice?: number;
}

export interface ICTOverlay {
  kind: 'ict';
  po3?: ICTPO3Phase[];
  killzones?: ICTKillzone[];
  /** Inverse Fair Value Gaps. */
  ifvg?: FairValueGap[];
  /** Optimal Trade Entry zone (typically 0.62-0.79 retracement). */
  ote?: { topPrice: number; bottomPrice: number; time?: number } | null;
  /** Market Structure Shift line. */
  mss?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
}

export interface CandlePatternMarker {
  pattern: 'pin-bar' | 'engulfing' | 'inside-bar' | 'doji' | 'hammer' | 'shooting-star';
  bias: 'bullish' | 'bearish';
  time: number;
  price: number;
}

export interface SwingPivot {
  type: 'high' | 'low';
  time: number;
  price: number;
}

export interface PriceActionOverlay {
  kind: 'price-action';
  pinBars?: CandlePatternMarker[];
  engulfings?: CandlePatternMarker[];
  pivots?: SwingPivot[];
  supply?: AnalysisZone[];
  demand?: AnalysisZone[];
}

export interface SMCOverlay {
  kind: 'smc';
  orderBlocks?: AnalysisZone[];
  fvg?: FairValueGap[];
  liquidity?: LiquidityPool[];
  bos?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
  choch?: { type: 'bullish' | 'bearish'; price: number; time: number } | null;
  keyLevels?: {
    recentHigh: number;
    recentLow: number;
    swingHigh: number;
    swingLow: number;
  };
}

export type MethodologyOverlay =
  | SMCOverlay
  | ICTOverlay
  | PriceActionOverlay
  | FibonacciOverlay;

/** Sentinel status emitted when the AI finds no actionable trade setup. */
export const NO_SETUP_STATUS = 'no_setup' as const;

export interface AnalysisResult {
  id: string;
  userId: string;
  symbol: string;
  timeframe: Timeframe;
  methodology: AnalysisMethodology;
  status: 'pending' | 'processing' | 'completed' | 'failed' | typeof NO_SETUP_STATUS | 'stale';
  // Flat convenience fields (also available in setup)
  direction?: 'long' | 'short' | null;
  confidence?: number | null;
  entry?: number | null;
  stopLoss?: number | null;
  riskReward?: number | null;
  // Performance metrics
  durationMs?: number;
  aiModel?: string;
  // Detailed analysis
  analysis: {
    trend: MarketTrend;
    trendStrength: number;
    marketPhase: MarketPhase;
    bias: 'long' | 'short' | 'neutral';
  };
  structure: {
    lastBos: {
      type: 'bullish' | 'bearish';
      price: number;
      time: number;
    } | null;
    lastChoch: {
      type: 'bullish' | 'bearish';
      price: number;
      time: number;
    } | null;
    keyLevels: {
      recentHigh: number;
      recentLow: number;
      swingHigh: number;
      swingLow: number;
    };
  };
  zones: {
    orderBlocks: AnalysisZone[];
    fvg: FairValueGap[];
    liquidity: LiquidityPool[];
  };
  setup: AnalysisSetup;
  narrative: string;
  chartMarkers: ChartMarker[];
  /**
   * Methodology-aware overlay payload. When present, the chart renders the
   * overlay matching `methodologyOverlay.kind`. When absent, falls back to the
   * legacy SMC-shaped `zones`/`structure` fields above (back-compat for old
   * analyses persisted before the v3 prompt templates rolled out).
   */
  methodologyOverlay?: MethodologyOverlay | null;
  createdAt: Date;
}

// Alert Types
export type AlertType = 'price' | 'setup' | 'target_hit' | 'stop_hit' | 'trend_change';
export type AlertCondition = 'gte' | 'lte' | 'eq' | 'cross_up' | 'cross_down';
export type AlertStatus = 'active' | 'triggered' | 'expired' | 'disabled';
export type NotificationChannel = 'telegram' | 'email' | 'whatsapp' | 'push';

export interface Alert {
  id: string;
  userId: string;
  type: AlertType;
  symbol: string;
  condition: AlertCondition;
  price: number;
  message?: string;
  channels: NotificationChannel[];
  status: AlertStatus;
  expiresAt?: Date;
  triggeredAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface AlertSettings {
  enabled: boolean;
  channels: {
    telegram?: {
      enabled: boolean;
      chatId: string;
      verified: boolean;
    };
    email?: {
      enabled: boolean;
      address: string;
      verified: boolean;
    };
    whatsapp?: {
      enabled: boolean;
      phone: string;
      verified: boolean;
    };
  };
  preferences: {
    priceAlerts: boolean;
    setupAlerts: boolean;
    targetAlerts: boolean;
    stopAlerts: boolean;
    quietHours?: {
      enabled: boolean;
      start: string;
      end: string;
      timezone: string;
    };
  };
}

// Language type
export type Language = 'en' | 'pt-BR' | 'es';

// Multi-model engine types
export type SelectionStrategy = 'fastest' | 'best';

// Queue Message Types
export interface AnalysisQueueMessage {
  id: string;
  userId: string;
  symbol: string;
  timeframe: Timeframe;
  methodology: AnalysisMethodology;
  aiModel: AIModel;
  language: Language;
  candles: Candle[];
  currentPrice: number;
  timestamp: number;
  models?: string[];
  selectionStrategy?: SelectionStrategy;
  /**
   * Optional UserPrompt id to append as an addendum to the official prompt.
   * Worker resolves the prompt and verifies ownership defensively — invalid
   * or foreign ids are ignored silently and the analysis proceeds normally.
   */
  userPromptId?: string;
}

// User Custom Prompt Types (Pro+ feature)
export type UserPromptMethodology = 'smc' | 'ict' | 'price_action' | 'fibonacci';

export interface UserPrompt {
  id: string;
  userId: string;
  name: string;
  content: string;
  methodology: UserPromptMethodology | null;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface NotificationQueueMessage {
  id: string;
  userId: string;
  type: 'alert' | 'setup' | 'system';
  channel: NotificationChannel;
  payload: {
    title: string;
    message: string;
    data?: Record<string, unknown>;
  };
  timestamp: number;
}

export interface PriceMonitorQueueMessage {
  alertId: string;
  symbol: string;
  condition: AlertCondition;
  targetPrice: number;
  currentPrice: number;
  timestamp: number;
}

// Trade Monitor Types
export type TradeStatus = 'PENDING' | 'ACTIVE' | 'WIN' | 'LOSS' | 'MISSED' | 'EXPIRED';

export type TradeMonitorEventType =
  | 'ENTRY_HIT'
  | 'STOP_LOSS_HIT'
  | 'TARGET_1_HIT'
  | 'TARGET_2_HIT'
  | 'TARGET_3_HIT'
  | 'EXPIRED'
  | 'MISSED';

export interface TradeMonitorQueueMessage {
  analysisId: string;
  userId: string;
  symbol: string;
  timeframe: Timeframe;
  direction: AnalysisDirection;
  entry: number;
  stopLoss: number;
  targets: {
    t1: { price: number; reasoning: string };
    t2: { price: number; reasoning: string };
    t3: { price: number; reasoning: string };
  };
  riskReward: number;
  expiresAt: number; // Unix timestamp
  language: Language;
  notificationChannels: NotificationChannel[];
}

export interface MonitoredTrade {
  analysisId: string;
  userId: string;
  symbol: string;
  timeframe: Timeframe;
  direction: AnalysisDirection;
  entry: number;
  stopLoss: number;
  targets: {
    t1: number;
    t2: number;
    t3: number;
  };
  riskReward: number;
  status: TradeStatus;
  expiresAt: number;
  language: Language;
  notificationChannels: NotificationChannel[];
  entryHitAt?: number;
  targetsHit: ('T1' | 'T2' | 'T3')[];
  maxTargetHit?: 'T1' | 'T2' | 'T3';
  lastPrice?: number;
  createdAt: number;
  // Statistics for trailing stop / breakeven analysis
  maxFavorableExcursion?: number; // Best price reached in favor (MFE)
  maxAdverseExcursion?: number;   // Worst price reached against (MAE)
  mfePips?: number;               // MFE in pips
  maePips?: number;               // MAE in pips
  breakevenHitAfterT1?: boolean;  // Did price return to entry after T1?
  trailingStopScenario?: {
    // What would have happened with trailing stop after T1
    wouldHaveExitedAt?: number;
    wouldHaveExitedPrice?: number;
    trailingPnlPips?: number;
  };
}

export interface TradeMonitorEvent {
  type: TradeMonitorEventType;
  trade: MonitoredTrade;
  price: number;
  timestamp: number;
  pnlPips?: number;
  actualRR?: number;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    hasMore?: boolean;
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// Auth Types
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  language?: 'en' | 'pt-BR' | 'es';
}

export interface TokenPayload {
  userId: string;
  email: string;
  plan: SubscriptionPlan;
  role: UserRole;
  language: Language;
  iat?: number;
  exp?: number;
}

// User Role Types
export type UserRole = 'user' | 'expert' | 'admin';

// Signal Types
export type SignalStatus = 'pending' | 'approved' | 'rejected';

export interface Signal {
  id: string;
  asset: string;
  timeframe: Timeframe;
  direction: AnalysisDirection;
  entryPrice: number;
  stopLoss: number;
  takeProfit1: number;
  takeProfit2?: number;
  takeProfit3?: number;
  riskReward: number;
  confidence: number;
  analysis: string;
  narrative?: string;
  /** Free-text commentary written by the expert when broadcasting the signal. */
  expertNotes?: string | null;
  status: SignalStatus;
  reviewedById?: string;
  reviewedBy?: {
    id: string;
    name: string;
  };
  reviewedAt?: Date;
  reviewNotes?: string;
  expiresAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateSignalRequest {
  asset: string;
  timeframe: Timeframe;
  direction: AnalysisDirection;
  entryPrice: number;
  stopLoss: number;
  takeProfit1: number;
  takeProfit2?: number;
  takeProfit3?: number;
  confidence: number;
  analysis: string;
  narrative?: string;
  /** Free-text guidance written by the expert at broadcast time. Max 500 chars. */
  expertNotes?: string;
  expiresAt?: string;
  /** When true and caller is admin/expert, signal is immediately approved and broadcast. */
  autoApprove?: boolean;
}

export interface ReviewSignalRequest {
  status: 'approved' | 'rejected';
  reviewNotes?: string;
}

export interface UserSignalNotification {
  id: string;
  userId: string;
  signalId: string;
  signal: Signal;
  viewedAt?: Date;
  dismissedAt?: Date;
  notifiedVia: string[];
  createdAt: Date;
}

export interface SignalNotificationQueueMessage {
  signalId: string;
  userId: string;
  channel: NotificationChannel;
  language: Language;
  timestamp: number;
  /** Expert commentary to append to per-channel notification bodies. */
  expertNotes?: string;
}

/**
 * Queue message for broadcasting a newly-approved signal to the public
 * Telegram channel. Contains only public-safe, non-user-specific fields.
 */
export interface SignalPublicBroadcastQueueMessage {
  signalId: string;
  asset: string;
  timeframe: Timeframe;
  direction: AnalysisDirection;
  confidence: number;
  /** The methodology/analysis label (e.g. "SMC"). Sourced from signal.analysis. */
  methodology: string;
  /** Truncated narrative excerpt — HTML stripped, max 120 chars. */
  narrativeExcerpt: string;
  /** Expert notes excerpt — HTML stripped, max 200 chars. Omitted when absent. */
  expertNotesExcerpt?: string;
  /** Source language of narrativeExcerpt/expertNotesExcerpt. Defaults to 'pt-BR' if absent. */
  sourceLanguage?: string;
  timestamp: number;
}

// Journal Types
export * from './journal.js';

// Trade Event Types
export * from './tradeEvent.js';

// Market Provider abstractions (CandleProvider, TickStreamProvider, AssetClass)
export * from './marketProvider.js';

/**
 * Message enqueued to auth.register.queue when a registration request is accepted.
 * The password field carries the plaintext password — transported over TLS at the
 * wire and on internal queue only. NEVER log this field.
 */
export interface RegisterQueueMessage {
  email: string;     // lowercased
  password: string;  // plaintext — never log
  name: string;
  language: string;
  timestamp: number;
  attemptId: string; // UUID for idempotency
}

export interface BatchAnalysisMessage {
  runId: string;
  asset: string;
  strategy: 'smc' | 'ict' | 'price_action' | 'fibonacci';
  timeframe: string;
  models: string[];
  language: string;
  /**
   * Per-config AI model override. When set, the batch worker uses this model
   * instead of the DEFAULT_AI_MODEL env var. Null/undefined means use the env
   * default.
   */
  aiModel?: string | null;
  /** Per-config confidence threshold (0–100). null/undefined = use global default (60). */
  confidenceThreshold?: number | null;
  /** Per-config minimum risk/reward ratio (> 0). null/undefined = use global default (1.5). */
  minRiskReward?: number | null;
}
